import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton, Skeleton, EmptyState, Segmented } from "@/components/ios";
import { ChevronLeft, GitCompare, Sparkles, ArrowRight, Scale, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const CHANGE_TONE = { damage: "danger", fair_wear_and_tear: "warning", cleaning: "accent", no_change: "success", no_comparison: "neutral" };
const CHANGE_LABEL = { damage: "Damage", fair_wear_and_tear: "Fair wear & tear", cleaning: "Cleaning", no_change: "No change", no_comparison: "No comparison" };
const DECISIONS = [
  { value: "damage", label: "Damage" },
  { value: "fair_wear_and_tear", label: "Fair wear" },
  { value: "cleaning", label: "Cleaning" },
  { value: "no_change", label: "No change" },
];

export default function Comparison() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/reports/${id}/comparison`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${id}/comparison/generate`);
      setData(data);
      toast.success("Entry vs exit comparison generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate comparison"); }
    finally { setBusy(false); }
  };

  const setLiability = async (item_key, decision) => {
    try {
      await api.put(`/reports/${id}/comparison/${item_key}`, { decision });
      toast.success(`Recorded: ${CHANGE_LABEL[decision]}`, { icon: <Scale size={16} /> });
      load();
    } catch (e) { toast.error("Could not save decision"); }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  if (!data.available)
    return (
      <div>
        <button onClick={() => nav(`/reports/${id}/review`)} className="flex items-center gap-1 text-sm text-secondary mb-4"><ChevronLeft size={16} /> Report</button>
        <Surface><EmptyState icon={GitCompare} title="Comparison unavailable" subtitle={data.reason} /></Surface>
      </div>
    );

  const comp = data.comparison;

  return (
    <div className="pb-16">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => nav(`/reports/${id}/review`)} className="flex items-center gap-1 text-sm text-secondary"><ChevronLeft size={16} /> Report</button>
        {comp && <AButton testid="regenerate-comparison" variant="text" onClick={generate} disabled={busy}>{busy ? "Analysing…" : "Regenerate"}</AButton>}
      </div>

      <div className="flex items-center gap-2 mb-1">
        <GitCompare className="accent-text" size={26} />
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Entry vs Exit Comparison</h1>
      </div>
      <p className="text-sm text-secondary mb-5">AI suggests damage vs fair wear-and-tear; you make the liability call.</p>

      {!data.has_entry && (
        <Surface className="p-4 mb-4"><p className="text-sm" style={{ color: "var(--warning)" }}>No prior entry report found for this property — nothing to compare against.</p></Surface>
      )}

      {!comp ? (
        <Surface><EmptyState icon={Sparkles} title="No comparison yet"
          subtitle="Generate an AI-assisted side-by-side of every photographed item at entry vs exit."
          action={<AButton testid="generate-comparison" onClick={generate} disabled={busy || !data.has_entry}>{busy ? "Analysing…" : "Generate comparison"}</AButton>} /></Surface>
      ) : comp.items.length === 0 ? (
        <Surface><EmptyState icon={GitCompare} title="No comparable items" subtitle="No items had photos at both entry and exit." /></Surface>
      ) : (
        <div className="space-y-4">
          {comp.items.map((it, idx) => {
            const ct = it.ai?.change_type || "no_comparison";
            return (
              <motion.div key={it.item_key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                <Surface className="p-4" data-testid={`compare-${it.item_key}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="font-semibold" style={{ color: "var(--text)" }}>{it.room} — {it.label}</span>
                    <Pill tone={CHANGE_TONE[ct]}><Sparkles size={10} /> AI: {CHANGE_LABEL[ct]}</Pill>
                    {it.ai?.confidence != null && <span className="text-xs text-secondary">{Math.round(it.ai.confidence * 100)}% conf.</span>}
                    {it.liability_call && <Pill tone={CHANGE_TONE[it.liability_call.decision]}><Scale size={10} /> Agent: {CHANGE_LABEL[it.liability_call.decision]}</Pill>}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {[["Entry", it.entry], ["Exit", it.exit]].map(([lbl, side]) => (
                      <div key={lbl}>
                        <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-1">{lbl}</div>
                        {side.signed_url ? (
                          <img src={mediaUrl(side.signed_url)} className="w-full h-44 object-cover rounded-xl" alt={lbl} />
                        ) : (
                          <div className="w-full h-44 rounded-xl grid place-items-center" style={{ background: "var(--surface-2)" }}>
                            <div className="text-center text-secondary text-xs"><ImageOff size={20} className="mx-auto mb-1" /> No {lbl.toLowerCase()} photo</div>
                          </div>
                        )}
                        <p className="text-xs text-secondary mt-1">{side.comment}</p>
                      </div>
                    ))}
                  </div>

                  {it.ai?.rationale && (
                    <div className="mt-3 text-sm p-3 rounded-lg flex items-start gap-2" style={{ background: "var(--surface-2)", color: "var(--text)" }}>
                      <Sparkles size={14} className="accent-text mt-0.5 shrink-0" /> {it.ai.rationale}
                    </div>
                  )}
                  {it.ai?.reason && ct === "no_comparison" && (
                    <div className="mt-3 text-xs text-secondary">{it.ai.reason}</div>
                  )}

                  {ct !== "no_comparison" && (
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-semibold text-secondary flex items-center gap-1"><Scale size={13} /> Your liability call:</span>
                      <Segmented testid={`liability-${it.item_key}`} value={it.liability_call?.decision || it.ai?.change_type}
                        onChange={(d) => setLiability(it.item_key, d)} options={DECISIONS} />
                    </div>
                  )}
                </Surface>
              </motion.div>
            );
          })}
          <p className="text-xs text-secondary text-center">AI suggestions are advisory. The agent's recorded decision is the liability determination and is written to the audit log.</p>
        </div>
      )}
    </div>
  );
}
